import { StateGauge } from './StateGauge.tsx'
import type { EtatStock } from '../lib/ops/index.ts'

/**
 * La ligne d'inventaire.
 *
 * La seconde ligne (quantité, note, DLC) n'est rendue QUE si elle a du
 * contenu. La plupart des lignes tiennent donc sur une seule : c'est ce qui
 * garde 200 articles lisibles.
 */
export function Row({
  nom,
  emoji,
  etat,
  detail,
  badge,
  onEtat,
  onOuvrir,
}: {
  nom: string
  emoji?: string | null
  etat: EtatStock
  detail?: string | null
  badge?: React.ReactNode
  onEtat: (e: EtatStock) => void
  onOuvrir?: () => void
}) {
  return (
    /*
     * Le séparateur est un trait en RETRAIT de 16 px à gauche, aligné sur le
     * texte — c'est ce retrait qui signale « ces lignes forment un groupe ».
     * Il est porté par la ligne elle-même, via un pseudo-élément — et NON
     * par un conteneur avec une marge, qui décalerait la ligne entière hors
     * de la carte (c'est le bug qu'on a vu à la première capture).
     */
    <div className="relative flex items-center bg-surface pr-2 first:rounded-t-md last:rounded-b-md [&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:inset-x-4 [&:not(:first-child)]:before:top-0 [&:not(:first-child)]:before:h-px [&:not(:first-child)]:before:bg-line">
      {/* Le rail : rien du tout pour « en stock ». C'est la règle centrale —
          seuls les écarts se signalent. */}
      {etat !== 'en_stock' && (
        <span
          aria-hidden
          className="rail"
          style={{
            backgroundColor: etat === 'epuise' ? 'var(--color-epuise)' : 'var(--color-bientot)',
          }}
        />
      )}

      <button
        type="button"
        onClick={onOuvrir}
        aria-label={`${nom} — ouvrir le détail`}
        className="pressable flex min-h-[56px] flex-1 items-center gap-3 py-2 pl-4 text-left"
      >
        {emoji && (
          <span aria-hidden className="w-6 shrink-0 text-center text-[17px]">
            {emoji}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-head text-ink">{nom}</span>
          {detail && <span className="mt-0.5 block truncate text-meta text-muted">{detail}</span>}
        </span>
        {badge}
        {/* Chevron discret : sans lui, rien ne dit que la ligne s'ouvre — et
            c'est précisément ce qui manquait pour retirer un article. */}
        {onOuvrir && (
          <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0 text-line-strong" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
        )}
      </button>

      <StateGauge etat={etat} onChange={onEtat} label={nom} />
    </div>
  )
}

/**
 * En-tête de groupe collant. Fond OPAQUE, pour que les lignes passent
 * proprement dessous au scroll — un fond translucide laisserait voir le
 * texte des lignes à travers, ce qui est le genre de détail qui fait
 * « site web ».
 */
export function EnTeteGroupe({ titre, emoji, compte }: { titre: string; emoji?: string | null; compte: number }) {
  return (
    <div className="sticky top-[var(--size-bar)] z-10 flex h-8 items-center justify-between bg-canvas px-1">
      <span className="text-label uppercase text-muted">
        {emoji && <span aria-hidden className="mr-1.5 not-uppercase">{emoji}</span>}
        {titre}
      </span>
      <span className="tnum text-label text-muted">{compte}</span>
    </div>
  )
}
